import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../shared/i18n';
import { childrenApi, dailyTargetsApi, familiesApi } from '../../api/guardians';
import GoalSetting from '../../../goal-setting/GoalSetting';
import { ChildSubscriptionModal } from '../ChildSubscriptionModal';

/**
 * Wrapper component that fetches child and daily target data,
 * then renders the GoalSetting component with proper props.
 */
export const GoalSettingWrapper = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Fetch child data
  const { data: childData, isLoading: childLoading, error: childError } = useQuery({
    queryKey: ['child', childId],
    queryFn: () => childrenApi.get(childId),
  });

  // Fetch daily target data
  const { data: targetData, isLoading: targetLoading, error: targetError } = useQuery({
    queryKey: ['dailyTarget', childId],
    queryFn: () => dailyTargetsApi.get(childId),
  });

  // Fetch family data for subscription modal
  const { data: familyData } = useQuery({
    queryKey: ['family'],
    queryFn: () => familiesApi.getFamily(),
  });

  const formatNumber = (num) => {
    return new Intl.NumberFormat('vi-VN').format(num);
  };

  // Build translations object for GoalSetting component
  const translations = {
    setDailyTargetFor: t('children.goal_setting.set_daily_target_for'),
    configureGoals: t('children.goal_setting.configure_goals'),
    dashboard: t('children.goal_setting.breadcrumbs.dashboard'),
    children: t('children.goal_setting.breadcrumbs.children'),
    setDailyTarget: t('children.goal_setting.breadcrumbs.set_daily_target'),
    dailyLearningTargets: t('children.goal_setting.mode_selector.daily_learning_targets'),
    chooseTargetMode: t('children.goal_setting.mode_selector.choose_target_mode'),
    basicMode: t('children.goal_setting.mode_selector.basic_mode'),
    sameTargetEveryDay: t('children.goal_setting.mode_selector.same_target_every_day'),
    advancedMode: t('children.goal_setting.mode_selector.advanced_mode'),
    differentTargetsPerDay: t('children.goal_setting.mode_selector.different_targets_per_day'),
    xpTarget: t('children.goal_setting.input_labels.xp_target'),
    selectDailyTarget: t('children.goal_setting.input_labels.select_daily_target'),
    day: t('children.goal_setting.input_labels.day'),
    xpGoal: t('children.goal_setting.input_labels.xp_goal'),
    xp100Description: t('children.goal_setting.xp_descriptions.xp100'),
    xp200Description: t('children.goal_setting.xp_descriptions.xp200'),
    xp500Description: t('children.goal_setting.xp_descriptions.xp500'),
    xp750Description: t('children.goal_setting.xp_descriptions.xp750'),
    xp1000Description: t('children.goal_setting.xp_descriptions.xp1000'),
    monday: t('children.goal_setting.days.monday'),
    tuesday: t('children.goal_setting.days.tuesday'),
    wednesday: t('children.goal_setting.days.wednesday'),
    thursday: t('children.goal_setting.days.thursday'),
    friday: t('children.goal_setting.days.friday'),
    saturday: t('children.goal_setting.days.saturday'),
    sunday: t('children.goal_setting.days.sunday'),
    backToDashboard: t('children.goal_setting.action_buttons.back_to_dashboard'),
    resetToDefault: t('children.goal_setting.action_buttons.reset_to_default'),
    saveTargetSettings: t('children.goal_setting.action_buttons.save_target_settings'),
    saving: t('children.goal_setting.action_buttons.saving'),
    targetTips: t('children.goal_setting.sidebar.target_tips'),
    tipAchievableGoals: t('children.goal_setting.sidebar.tip_achievable_goals'),
    tipGradualIncrease: t('children.goal_setting.sidebar.tip_gradual_increase'),
    tipWeekendDifferent: t('children.goal_setting.sidebar.tip_weekend_different'),
    quickActions: t('children.goal_setting.sidebar.quick_actions'),
    applyToAllChildren: t('children.goal_setting.sidebar.apply_to_all_children'),
    // Upgrade modal
    upgradeModalTitle: t('children.goal_setting.upgrade_modal.title'),
    upgradeModalDescription: t('children.goal_setting.upgrade_modal.description'),
    upgradeModalClose: t('children.goal_setting.upgrade_modal.close'),
    upgradeModalUpgrade: t('children.goal_setting.upgrade_modal.upgrade'),
  };

  if (childLoading || targetLoading) {
    return (
      <div className="d-flex min-vh-100 bg-light align-items-center justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (childError || targetError) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          {childError?.message || targetError?.message || 'Failed to load data'}
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const child = childData?.data?.child;
  const dailyTarget = targetData?.data?.daily_target;
  // Use server-provided default, no hardcoded fallback needed since API always provides it
  const defaultXp = dailyTarget?.default_target_xp;

  if (!child) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">Child not found</div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  // Convert API response to GoalSetting props format
  // API already provides defaults, so these fallbacks are just for safety
  const initialWeekdayXp = {
    mondayXp: dailyTarget?.monday_xp,
    tuesdayXp: dailyTarget?.tuesday_xp,
    wednesdayXp: dailyTarget?.wednesday_xp,
    thursdayXp: dailyTarget?.thursday_xp,
    fridayXp: dailyTarget?.friday_xp,
    saturdayXp: dailyTarget?.saturday_xp,
    sundayXp: dailyTarget?.sunday_xp,
  };

  const family = familyData?.data?.family;

  return (
    <>
      <GoalSetting
        childId={child.id}
        childName={child.username}
        childAvatar={child.avatar}
        childZodiacName={child.zodiac_name}
        initialMode={dailyTarget?.mode || 'basic'}
        initialTargetXp={dailyTarget?.target_xp}
        initialWeekdayXp={initialWeekdayXp}
        isUpdate={dailyTarget?.is_persisted || false}
        dashboardPath="/guardian"
        translations={translations}
        defaultTargetXp={defaultXp}
        hasSubscription={child.subscribed}
        maxFreeXp={dailyTarget?.max_free_xp}
        onUpgrade={() => setShowSubscriptionModal(true)}
      />
      {showSubscriptionModal && family && (
        <ChildSubscriptionModal
          child={child}
          family={family}
          mode="subscribe"
          onClose={() => setShowSubscriptionModal(false)}
          t={t}
          formatNumber={formatNumber}
        />
      )}
    </>
  );
};

export default GoalSettingWrapper;
